import * as stylex from "@stylexjs/stylex";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import { useContext } from "react";
import type { Components } from "react-markdown";
import { MarkdownContext } from "./markdown-context";

export const markdownElements = {
  p: function MarkdownParagraph({ children }) {
    const { variant } = useContext(MarkdownContext);
    return (
      <p
        {...stylex.props(
          styles.paragraph,
          variant === "message" && styles.messageBlock,
          variant === "article" && styles.articleBlock,
        )}
      >
        {children}
      </p>
    );
  },
  ul: function MarkdownUnorderedList({ children }) {
    const { variant } = useContext(MarkdownContext);
    return (
      <ul
        {...stylex.props(
          styles.list,
          styles.unorderedList,
          variant === "message" && styles.messageBlock,
          variant === "article" && styles.articleBlock,
          variant === "article" && styles.articleList,
        )}
      >
        {children}
      </ul>
    );
  },
  ol: function MarkdownOrderedList({ children, start }) {
    const { variant } = useContext(MarkdownContext);
    return (
      <ol
        start={start}
        {...stylex.props(
          styles.list,
          styles.orderedList,
          variant === "message" && styles.messageBlock,
          variant === "article" && styles.articleBlock,
          variant === "article" && styles.articleList,
        )}
      >
        {children}
      </ol>
    );
  },
  li: function MarkdownItem({ children }) {
    const article = useContext(MarkdownContext).variant === "article";
    return (
      <li {...stylex.props(styles.item, article && styles.articleItem)}>
        {children}
      </li>
    );
  },
  strong: function MarkdownStrong({ children }) {
    const article = useContext(MarkdownContext).variant === "article";
    return (
      <strong {...stylex.props(article && styles.articleStrong)}>
        {children}
      </strong>
    );
  },
  blockquote: function MarkdownQuote({ children }) {
    const { variant } = useContext(MarkdownContext);
    return (
      <blockquote
        {...stylex.props(
          styles.quote,
          variant === "message" && styles.messageBlock,
          variant === "article" && styles.articleQuote,
        )}
      >
        {children}
      </blockquote>
    );
  },
  hr: function MarkdownRule() {
    const { variant } = useContext(MarkdownContext);
    return (
      <hr
        {...stylex.props(
          styles.rule,
          variant === "message" && styles.messageBlock,
          variant === "article" && styles.articleRule,
        )}
      />
    );
  },
} satisfies Components;

const styles = stylex.create({
  paragraph: { marginBlock: spacing.space16 },
  list: { paddingLeft: spacing.space24, marginBlock: spacing.space16 },
  unorderedList: { listStyleType: "disc" },
  orderedList: { listStyleType: "decimal" },
  item: { marginBlock: spacing.space4 },
  quote: {
    borderLeftWidth: 1,
    borderLeftStyle: "solid",
    borderLeftColor: colors.borderStrong,
    paddingLeft: spacing.space16,
    marginInline: 0,
    marginBlock: spacing.space20,
    color: colors.subtle,
  },
  rule: {
    borderWidth: 0,
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: colors.borderStrong,
    marginBlock: spacing.space24,
  },
  // A message keeps its blocks close and flush with its card's edges.
  messageBlock: {
    marginBlock: 0,
    marginTop: { default: spacing.space8, ":first-child": 0 },
  },
  articleBlock: { marginTop: 0, marginBottom: "0.5lh" },
  articleList: { paddingLeft: 22 },
  articleItem: { marginBlock: 0, marginBottom: 10 },
  articleStrong: { color: colors.foreground, fontWeight: 600 },
  articleQuote: {
    borderLeftWidth: 3,
    borderLeftColor: colors.overlayBorder,
    color: "inherit",
    fontStyle: "italic",
    marginBlock: 24,
    paddingLeft: 18,
  },
  articleRule: { borderTopColor: colors.border, marginBlock: 40 },
});
